<template>
  <teleport to="body">
    <div
      v-if="show"
      class="modal d-block action-captcha-overlay"
      tabindex="-1"
      role="dialog"
      aria-modal="true"
      :aria-label="title"
      @click.self="handleCancel"
    >
      <div class="modal-dialog modal-dialog-centered" role="document">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">{{ title }}</h5>
            <button
              type="button"
              class="btn-close"
              aria-label="Close"
              :disabled="loading"
              @click="handleCancel"
            ></button>
          </div>

          <div class="modal-body">
            <p class="mb-2">{{ promptText }}</p>
            <div class="d-flex align-items-center gap-2 mb-3 captcha-row">
              <div class="svg-box" v-html="firstNumberSvg"></div>
              <span class="math-symbol">+</span>
              <div class="svg-box" v-html="secondNumberSvg"></div>
              <span class="math-symbol">=</span>
            </div>

            <label class="form-label fw-semibold" for="captcha-answer">Your answer</label>
            <input
              id="captcha-answer"
              ref="answerInput"
              v-model.number="answer"
              class="form-control"
              type="number"
              inputmode="numeric"
              step="1"
              min="0"
              :disabled="loading"
              @keyup.enter="verify"
            />

            <p v-if="errorMessage" class="text-danger mt-2 mb-0">{{ errorMessage }}</p>
          </div>

          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-outline-secondary"
              :disabled="loading"
              @click="refreshChallenge"
            >
              Refresh
            </button>
            <button
              type="button"
              class="btn btn-secondary"
              :disabled="loading"
              @click="handleCancel"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              :disabled="loading"
              @click="verify"
            >
              {{ confirmLabel }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </teleport>
</template>

<script>
export default {
  name: 'ActionCaptchaModal',
  props: {
    show: {
      type: Boolean,
      default: false
    },
    title: {
      type: String,
      default: 'Confirm Action'
    },
    promptText: {
      type: String,
      default: 'Please solve this quick check to continue.'
    },
    confirmLabel: {
      type: String,
      default: 'Confirm'
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'confirmed', 'failed'],
  data() {
    return {
      firstNumber: 10,
      secondNumber: 0,
      answer: null,
      errorMessage: ''
    };
  },
  computed: {
    expectedSum() {
      return this.firstNumber + this.secondNumber;
    },
    firstNumberSvg() {
      return this.makeNumberSvg(this.firstNumber);
    },
    secondNumberSvg() {
      return this.makeNumberSvg(this.secondNumber);
    }
  },
  watch: {
    show(newValue) {
      if (newValue) {
        this.refreshChallenge();
        this.$nextTick(() => {
          if (this.$refs.answerInput) this.$refs.answerInput.focus();
        });
      }
    }
  },
  methods: {
    makeNumberSvg(value) {
      const color = '#1f2937';
      return [
        '<svg xmlns="http://www.w3.org/2000/svg" width="96" height="44" viewBox="0 0 96 44" role="img" aria-label="Captcha number">',
        '<rect x="1" y="1" width="94" height="42" rx="8" fill="#f8fafc" stroke="#d1d5db"/>',
        '<text x="48" y="29" text-anchor="middle" font-size="24" font-weight="700" fill="' + color + '" font-family="monospace">' + String(value) + '</text>',
        '</svg>'
      ].join('');
    },
    randomTwoDigit() {
      return Math.floor(Math.random() * 90) + 10;
    },
    randomOneDigit() {
      return Math.floor(Math.random() * 10);
    },
    refreshChallenge() {
      this.firstNumber = this.randomTwoDigit();
      this.secondNumber = this.randomOneDigit();
      this.answer = null;
      this.errorMessage = '';
      this.$nextTick(() => {
        if (this.$refs.answerInput) this.$refs.answerInput.focus();
      });
    },
    handleCancel() {
      this.errorMessage = '';
      this.$emit('close');
    },
    verify() {
      if (this.loading) return;

      if (this.answer === null || this.answer === '' || Number.isNaN(Number(this.answer))) {
        this.errorMessage = 'Please enter a number.';
        return;
      }

      if (Number(this.answer) === this.expectedSum) {
        this.errorMessage = '';
        this.$emit('confirmed', {
          firstNumber: this.firstNumber,
          secondNumber: this.secondNumber,
          expectedSum: this.expectedSum
        });
        return;
      }

      this.errorMessage = 'Incorrect sum. Please try again.';
      this.$emit('failed', {
        firstNumber: this.firstNumber,
        secondNumber: this.secondNumber,
        expectedSum: this.expectedSum,
        providedAnswer: Number(this.answer)
      });
      this.refreshChallenge();
    }
  }
};
</script>

<style scoped>
.action-captcha-overlay {
  background: rgba(15, 23, 42, 0.55);
  z-index: 3000;
}

.action-captcha-overlay .modal-dialog {
  z-index: 3001;
}

.captcha-row {
  flex-wrap: wrap;
}

.math-symbol {
  font-size: 1.4rem;
  font-weight: 700;
  color: #1f2937;
}

.svg-box {
  line-height: 0;
}
</style>
